import React, { useEffect, useState } from 'react';
import { StyledEngineProvider, createTheme } from '@mui/material/styles';
import { ThemeProvider } from '@mui/styles';
import _ from 'lodash';

import Routes from './Routes';
import { LoadingSpinner } from './component/ui-components/loading-spinner.component';
import { Alert } from './component/ui-components/alert.component';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from './component/ui-components/error-fallback.component';
import { LoggerProvider, useLogger } from './services/logging/logger-context';
import {
  useFetchAPIPrefixQuery,
  useFetchStripeKeyQuery,
  useFetchCognitoQuery,
} from './services/deployment.api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { StaticHelper } from './util/static.helper';
import { useFetchReceiptSettingsQuery, useFetchThemeSettingsQuery } from './services/env.api';
import { AuthProvider } from './hooks/useAuth';
import { I18nextProvider } from 'react-i18next';
import { i18n } from './services/translation/i18n';
import { DomainProvider, useDomain } from './hooks/useDomain';
import { defaultTheme } from './styles/styles.util';
import { UserPreferenceProvider } from './hooks/useUserPreference';
import { elementFontOptions } from './component/payments/utils';
import posthog from 'posthog-js';

const WhiteLabellingApp = () => {
  const { logger } = useLogger();
  const { domain } = useDomain();
  const { data: deploymentData, isSuccess: isDeploymentDataSuccess } = useFetchAPIPrefixQuery();
  const { data: stripeData, isSuccess: isStripeDataSuccess } = useFetchStripeKeyQuery();
  const { data: cognitoData, isSuccess: isCognitoDataSuccess } = useFetchCognitoQuery();
  const { data: theme, isSuccess: isThemeSuccess } = useFetchThemeSettingsQuery();
  const [finalTheme, setFinalTheme] = useState(defaultTheme);

  useEffect(() => {
    setFinalTheme(createTheme(_.merge(defaultTheme, theme)));
  }, [isThemeSuccess, theme]);

  const { data: receiptSetting, isSuccess: isReceiptSettingsSuccess } =
    useFetchReceiptSettingsQuery();

  useEffect(() => {
    if (deploymentData?.postHogApiKey && deploymentData?.postHogApiHost) {
      posthog.init(deploymentData.postHogApiKey, {
        api_host: deploymentData.postHogApiHost,
        opt_in_site_apps: true,
      });
    }
  }, [deploymentData?.postHogApiKey, deploymentData?.postHogApiHost]);

  useEffect(() => {
    if (isReceiptSettingsSuccess) {
      // tab title will be determined based on the domain
      document.title = receiptSetting.companyName || '';
      const descMeta = document.getElementsByName('description');
      if (descMeta && descMeta.length > 0) {
        descMeta[0].textContent = receiptSetting.companyName || '';
      }
    }
  }, [receiptSetting, isReceiptSettingsSuccess]);

  useEffect(() => {
    if (isStripeDataSuccess) {
      if (!StaticHelper.isStripeInited) {
        StaticHelper.stripe = loadStripe(stripeData.api_key);
        StaticHelper.isStripeInited = true;
      }
    }
  }, [isStripeDataSuccess, stripeData]);

  useEffect(() => {
    const href = `/assets?resourceId=favicon&domainName=${domain}`;
    const element = document.getElementById('favicon') as HTMLAnchorElement | null;
    if (element) {
      element.href = href;
    }
    if (document) {
      document.documentElement.lang = i18n.language;
    }
    const touchIcon = document.getElementById('touchicon') as HTMLAnchorElement | null;
    if (touchIcon) {
      touchIcon.href = href;
    }
  }, [domain]);

  if (
    !isDeploymentDataSuccess ||
    !StaticHelper.isStripeInited ||
    !isThemeSuccess ||
    !isCognitoDataSuccess
  ) {
    return null;
  }
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, errorInfo) => {
        logger.error('ErrorBoundary caught an error:', error, errorInfo.componentStack);
      }}
    >
      <ThemeProvider theme={finalTheme}>
        <I18nextProvider i18n={i18n}>
          <AuthProvider cognitoData={cognitoData}>
            <StyledEngineProvider injectFirst>
              <Elements options={elementFontOptions} stripe={StaticHelper.stripe}>
                <UserPreferenceProvider>
                  <Routes />
                  <Alert />
                  <LoadingSpinner />
                </UserPreferenceProvider>
              </Elements>
            </StyledEngineProvider>
          </AuthProvider>
        </I18nextProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

const App = () => {
  return (
    <React.StrictMode>
      <LoggerProvider>
        <DomainProvider>
          <WhiteLabellingApp />
        </DomainProvider>
      </LoggerProvider>
    </React.StrictMode>
  );
};

export default App;
